import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CalendarDays, ChevronLeft, ChevronRight, Trash2, X, Briefcase } from "lucide-react";
import { format, addDays, startOfWeek } from "date-fns";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import AssignShiftDialog from "@/components/AssignShiftDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GroupScheduleTab = ({ employees }) => {
  const { t } = useLanguage();
  const [weekSchedule, setWeekSchedule] = useState(null);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);

  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const fetchWeekSchedule = useCallback(async () => {
    try {
      const dateStr = format(weekStart, 'yyyy-MM-dd');
      const res = await fetch(`${API_URL}/api/schedule/week?week_start=${dateStr}`, { credentials: 'include' });
      if (res.ok) setWeekSchedule(await res.json());
    } catch (e) { console.error(e); }
  }, [weekStart]);

  useEffect(() => { fetchWeekSchedule(); }, [fetchWeekSchedule]);

  const goToPrevWeek = () => setWeekStart(addDays(weekStart, -7));
  const goToNextWeek = () => setWeekStart(addDays(weekStart, 7));
  const weekEndDate = addDays(weekStart, 6);
  const weekLabel = `${format(weekStart, 'MMM d')} - ${format(weekEndDate, 'MMM d, yyyy')}`;
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const handleClearAllShifts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/employer/shifts/clear`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        toast.success("All shifts cleared");
        fetchWeekSchedule();
        setShowClearDialog(false);
      }
    } catch (e) { toast.error("Failed to clear shifts"); }
  };

  const handleDeleteShift = async (shiftId) => {
    try {
      const res = await fetch(`${API_URL}/api/shifts/${shiftId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        toast.success("Shift deleted");
        fetchWeekSchedule();
      }
    } catch (e) { toast.error("Failed to delete shift"); }
  };

  const handleDeleteTimeOff = async (requestId) => {
    try {
      const res = await fetch(`${API_URL}/api/timeoff/${requestId}/revoke`, { 
        method: 'POST', 
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        toast.success(t("timeOffRemoved") || "Time off removed");
        fetchWeekSchedule();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to remove time off");
      }
    } catch (e) { toast.error("Failed to remove time off"); }
  };

  const renderScheduleContent = () => {
    if (!weekSchedule) return <p className="text-center text-muted-foreground py-8">{t("loading") || "Loading..."}</p>;
    
    const dates = Object.keys(weekSchedule.schedule);
    const emps = weekSchedule.employees || [];
    const payType = weekSchedule.pay_type || "hourly";
    
    if (emps.length === 0) {
      return <div className="text-center py-12 bg-background"><p className="text-muted-foreground">{t("noEmployeesYet") || "No employees yet"}</p></div>;
    }

    return (
      <div className="overflow-x-auto">
        {payType === "salary" && (
          <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Briefcase className="w-4 h-4" />
            <span>{t("salaryScheduleNote") || "Showing permanent schedules. Shift adjustments and time-off override permanent times."}</span>
          </div>
        )}
        <div className="min-w-[800px]">
          {/* Header */}
          <div className="grid grid-cols-8 gap-px bg-border rounded-t-lg overflow-hidden">
            <div className="bg-muted p-3 font-medium text-sm">{t("employee") || "Employee"}</div>
            {dates.map((date, i) => {
              // Parse date without timezone conversion
              const [year, month, day] = date.split('-').map(Number);
              const dateObj = new Date(year, month - 1, day);
              return (
              <div key={date} className={`bg-muted p-3 text-center ${todayStr === date ? 'bg-primary/10' : ''}`}>
                <p className="font-medium text-sm">{dayNames[i]}</p>
                <p className="text-xs text-muted-foreground">{format(dateObj, 'MMM d')}</p>
              </div>
              );
            })}
          </div>
          
          {/* Employee rows */}
          {emps.map((emp) => (
            <div key={emp.user_id} className="grid grid-cols-8 gap-px bg-border">
              <div className="bg-background p-3 flex items-center gap-2">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-accent text-xs">
                    {(emp.display_name || emp.name || "?").charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium truncate">{emp.display_name || emp.name}</span>
              </div>
              {dates.map((date) => {
                const dayData = weekSchedule.schedule[date];
                const isToday = todayStr === date;
                return (
                  <ScheduleCell 
                    key={date} 
                    dayData={dayData} 
                    userId={emp.user_id} 
                    isToday={isToday} 
                    t={t}
                    onDeleteShift={handleDeleteShift}
                    onDeleteTimeOff={handleDeleteTimeOff}
                    payType={payType}
                  />
                );
              })}
            </div>
          ))}
        </div>
        
        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4 text-xs">
          {payType === "salary" && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-100 border-l-4 border-blue-500 rounded-r"></div>
              <span>{t("permanentSchedule") || "Permanent Schedule"}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-teal-100 border-l-4 border-teal-500 rounded-r"></div>
            <span>{t("shiftAdjustment") || "Shift / Adjustment"}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-purple-100 border-l-4 border-purple-500 rounded-r"></div>
            <span>{t("timeOff") || "Time Off"}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                {t("weeklySchedule") || "Weekly Schedule"}
              </CardTitle>
              <CardDescription>View and manage your team schedule</CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={goToPrevWeek}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm font-medium px-2">{weekLabel}</span>
              <Button variant="outline" size="sm" onClick={goToNextWeek}><ChevronRight className="w-4 h-4" /></Button>
              <Button size="sm" onClick={() => setShowAssignDialog(true)} data-testid="assign-shift-btn">
                {t("assignShift") || "Assign Shift"}
              </Button>
              <Button size="sm" variant="destructive" onClick={() => setShowClearDialog(true)}>
                <Trash2 className="w-4 h-4 mr-1" />Clear All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {renderScheduleContent()}
        </CardContent>
      </Card>

      <AssignShiftDialog 
        open={showAssignDialog} 
        onOpenChange={setShowAssignDialog} 
        employees={employees} 
        onSuccess={fetchWeekSchedule} 
      />

      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Shifts?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove all scheduled shifts for all employees. The data will be archived for audit purposes. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearAllShifts} className="bg-destructive text-destructive-foreground">Clear All Shifts</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const ScheduleCell = ({ dayData, userId, isToday, t, onDeleteShift, onDeleteTimeOff, payType }) => {
  const shifts = [];
  const timeOffs = [];
  const permanent = [];
  
  if (dayData && dayData.shifts) {
    for (let i = 0; i < dayData.shifts.length; i++) {
      if (dayData.shifts[i].user_id === userId) {
        shifts.push(dayData.shifts[i]);
      }
    }
  }
  
  if (dayData && dayData.time_off) {
    for (let i = 0; i < dayData.time_off.length; i++) {
      if (dayData.time_off[i].user_id === userId) {
        timeOffs.push(dayData.time_off[i]);
      }
    }
  }
  
  if (dayData && dayData.permanent) {
    for (let i = 0; i < dayData.permanent.length; i++) {
      if (dayData.permanent[i].user_id === userId) {
        permanent.push(dayData.permanent[i]);
      }
    }
  }

  return (
    <div className={`bg-background p-2 min-h-[80px] ${isToday ? 'bg-primary/5' : ''}`}>
      {/* Permanent schedules (salary companies) - shown first with different color */}
      {permanent.map((p, idx) => (
        <div key={`perm-${idx}`} className="text-xs p-2 mb-1 bg-blue-100 border-l-4 border-blue-500 rounded-r">
          <p className="font-medium text-blue-700">{p.start_time} - {p.end_time}</p>
        </div>
      ))}
      {/* Shifts (adjustments) */}
      {shifts.map((s, idx) => (
        <div key={idx} className="text-xs p-2 mb-1 bg-teal-100 border-l-4 border-teal-500 rounded-r group relative">
          <button 
            onClick={() => onDeleteShift(s.shift_id)} 
            className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="w-3 h-3" />
          </button>
          <p className="font-medium text-teal-700">{s.start_time} - {s.end_time}</p>
        </div>
      ))}
      {/* Time off - with delete button */}
      {timeOffs.map((item, idx) => (
        <div key={idx} className="text-xs p-2 bg-purple-100 border-l-4 border-purple-500 rounded-r group relative">
          <button 
            onClick={() => onDeleteTimeOff(item.request_id)} 
            className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            title={t("removeTimeOff") || "Remove time off"}
          >
            <X className="w-3 h-3" />
          </button>
          <p className="font-medium text-purple-700">{t("timeOff") || "Time Off"}</p>
          {item.reason && <p className="text-purple-600 text-[10px]">{item.reason}</p>}
        </div>
      ))}
    </div>
  );
};

export default GroupScheduleTab;
